import { roundToStepSize } from "../../../utils/format";

export interface SpotOrderSizeInput {
  /** Base-token size the trader's input converts to, before step alignment. */
  rawSize: number;
  amountStep: number;
  amountPrecision: number;
  /** The market's hard minimum order quantity, when it reports one. */
  minOrderAmount?: number;
}

export type SpotOrderSizeResult =
  | { size: number; belowMinimum?: false }
  | { size: 0; belowMinimum: true; minimum: number };

/**
 * The base size to send for a spot order.
 *
 * Always rounded DOWN to the market's `amount_step`: on a BUY the trader must
 * never spend more quote than they entered, and on a SELL never sell more base
 * than they entered. (A 2.6 USDC buy was rounded UP to 0.0011 ETH and cost
 * $2.72.) When rounding down leaves less than the market's minimum, the caller
 * blocks the order — rounding UP to reach the minimum would spend more than the
 * trader asked for.
 */
export const resolveSpotOrderSize = ({
  rawSize,
  amountStep,
  amountPrecision,
  minOrderAmount,
}: SpotOrderSizeInput): SpotOrderSizeResult => {
  const size = roundToStepSize(rawSize, amountStep, amountPrecision);
  const minimum = Number(minOrderAmount) || 0;
  if (size <= 0 || (minimum > 0 && size < minimum)) {
    return { size: 0, belowMinimum: true, minimum: minimum || amountStep };
  }
  return { size };
};
